import type { CSSProperties } from 'react';
import { generateSrcSet } from '../lib/image-src-set';
import type { NodeProps } from '../types';

export type ContentImageProps = NodeProps<{
  src: string;
  id: string;
  naturalWidth: number;
  naturalHeight: number;
  alt?: string;
  caption?: string;
  title?: string;
  align?: 'left' | 'center' | 'right';
  // ponytail: intentionally unapplied. Legacy ContentImage set spacing as the CSS
  // `top`/`bottom` longhands on a `position: static` <span> (see structured-content
  // ContentImage/index.tsx), where they are inert — a silent no-op rendering zero
  // spacing. Dropping it is pixel-identical to legacy; applying it as mt-/mb- would
  // REGRESS parity by adding spacing legacy never showed. Kept on the type for schema fidelity.
  spacing?: { top?: number; bottom?: number };
}>;

export function ContentImage({ props }: ContentImageProps) {
  const srcSet = generateSrcSet({
    source: props.src,
    width: props.naturalWidth,
    height: props.naturalHeight,
  });

  if (!srcSet) return null;

  const alignment = {
    center: 'mx-auto',
    left: 'mx-auto md:float-left md:mr-4',
    right: 'mx-auto md:float-right md:ml-4',
  }[props.align ?? 'center'];

  const image = (
    <img
      alt={props.alt}
      className="h-auto w-full object-contain sm:h-[var(--image-height)] sm:w-[var(--image-width)]"
      height={props.naturalHeight}
      id={props.id}
      loading="lazy"
      srcSet={srcSet}
      width={props.naturalWidth}
    />
  );

  return (
    <figure
      className={`block max-w-[var(--image-width)] ${alignment}`}
      style={
        {
          '--image-height': `${props.naturalHeight}px`,
          '--image-width': `${props.naturalWidth}px`,
        } as CSSProperties
      }
    >
      {image}
      {props.caption && (
        <figcaption className="typography-body-small text-muted-foreground">
          {props.caption}
        </figcaption>
      )}
    </figure>
  );
}
